export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const UTM_FIRST_TOUCH_KEY = "utm_first_touch";

export function readUtmsFromUrl(): UtmParams {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const utms: UtmParams = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) utms[key] = v;
  }
  return utms;
}

export function getStoredFirstTouchUtms(): UtmParams {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(UTM_FIRST_TOUCH_KEY);
    return raw ? (JSON.parse(raw) as UtmParams) : {};
  } catch {
    return {};
  }
}

export function storeUtmsIfFirstTouch(utms: UtmParams): void {
  if (typeof window === "undefined") return;
  if (Object.keys(utms).length === 0) return;
  try {
    if (window.localStorage.getItem(UTM_FIRST_TOUCH_KEY)) return;
    window.localStorage.setItem(UTM_FIRST_TOUCH_KEY, JSON.stringify(utms));
  } catch {
    /* ignore */
  }
}

/**
 * Build a host-domain string for `utm_term` from the page that embedded us.
 *
 * When the embed iframe is loaded, `document.referrer` inside the iframe is
 * the host page URL (the page that contains the `<iframe src="…/embed/…">`),
 * because that page navigated the iframe to our URL. This is exactly the
 * "which site is embedding my podcast" signal we want to attribute.
 *
 * Returns `null` if the referrer is empty, same-origin (direct nav), or not a
 * valid http(s) URL — callers should treat `null` as "no host attribution".
 */
export function detectHostDomainFromReferrer(): string | null {
  if (typeof window === "undefined") return null;
  const ref = window.document.referrer;
  if (!ref) return null;
  try {
    const u = new URL(ref);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    if (typeof location !== "undefined" && u.host === location.host) return null;
    return u.host;
  } catch {
    return null;
  }
}

/**
 * Compose a full URL with the given UTM params appended. Existing `utm_*`
 * keys on the destination URL are stripped first so the new values always win
 * (useful when re-tagging links that may already carry stale UTMs).
 *
 * Pass `null` for `term` to skip it; the resolver is the caller's job so this
 * helper stays SSR-safe and synchronous.
 */
export function buildUtmUrl(
  baseUrl: string,
  utms: UtmParams,
  options?: { stripExisting?: boolean },
): string {
  const strip = options?.stripExisting ?? true;
  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch {
    return baseUrl;
  }
  if (strip) {
    for (const k of UTM_KEYS) url.searchParams.delete(k);
  }
  for (const k of UTM_KEYS) {
    const v = utms[k];
    if (typeof v === "string" && v.length > 0) url.searchParams.set(k, v);
  }
  return url.toString();
}
